(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // Apex cart-metafield sync (Storefront API, metafields ONLY).
  //
  // Does NOT touch cart *attributes* — the enrollment attribution
  // (cart-attributes.liquid, custom-scripts.liquid, from-param-tracker.liquid,
  // cookie logic in custom.js) owns cart attributes. Writing only cart
  // *metafields* here keeps the two systems from colliding.
  //
  // Volumes come from each product's own metafields (static product data) ×
  // line quantity — NOT from line-item properties (mutated by cart transforms /
  // discounts) and NOT from cart attributes (overwritten by other functions).
  //
  // Metafields written (namespace `apex`, owner = Cart), mirrored to the order
  // via metafield definitions with cart-to-order copy enabled:
  //   apex.cart_lines (json) — every line item + props + per-line volumes
  //     [{ key, variant_id, product_id, quantity, properties,
  //        volume1, volume2, volume3,  (per-unit, from product metafields — always shown)
  //        counted,              (false when excluded by COUNT_SUBSCRIPTION_VOLUME)
  //        volume1_total, volume2_total, volume3_total }]  (per-unit × qty, or 0 when not counted;
  //                                                         volume2_total truncated to a whole number,
  //                                                         volume1/3_total rounded to 4 decimals)
  //   apex.volume1 (number_decimal)  Retail    — Σ volume1_total
  //   apex.volume2 (number_decimal)  Business  — Σ volume2_total
  //   apex.volume3 (number_decimal)  Wholesale — Σ volume3_total
  // A future-dated subscription line is dropped from the totals unless
  // COUNT_SUBSCRIPTION_VOLUME is true (see that flag below).
  //   apex.sponsor (single_line_text_field)          — resolved sponsor rep # (always)
  //   apex.referral_key (single_line_text_field)     — label: how the visitor arrived
  //   apex.referral_value (single_line_text_field)   — ?from / ?cilikey value, or sponsor id
  //   apex.delivery_date (single_line_text_field)    — #deliveryDate preferred date
  //   apex.enrollment_type (single_line_text_field)  — mirrors the enrollment_type cart attribute
  //   apex.has_member_id (boolean) — logged-in customer has an apex.member_id
  //     (from apex-config). Always written true/false whenever the cart has
  //     items, so a flag from a previous login can't linger. Read by the
  //     checkout Business Volume block for display only; not copied to the order.
  // referral_key + referral_value supersede the old share_key / from_source.
  //
  // volume1/2/3 are always exactly the sum of the per-line *_total fields in the
  // JSON, so the two can be cross-checked against each other and against a
  // product-metafield recompute (see the Shopify Flow that guards this).
  //
  // No-op unless a Storefront API token is configured in the theme settings.
  // REQUIRES the apex.volume1 / volume2 / volume3 metafield definitions to have
  // Storefront API access enabled, otherwise the lookup returns null and volumes
  // (both totals and per-line) come back 0.
  // -------------------------------------------------------------------------

  var CONFIG_NODE_ID = 'apex-config';
  var CART_TOKEN_ATTRIBUTE = 'CartToken';
  var SS_MF_HASH_KEY = 'apex_cart_mf_hash';
  var LATE_SYNC_MS = 2500;
  var SF_ENDPOINT = '/api/2025-07/graphql.json';

  // Toggle — do we count subscription volume on this order?
  //   true  → subscription lines always count.
  //   false → a subscription line counts ONLY if its start date is today (it
  //           charges now); a future-dated subscription is excluded (its volume
  //           is credited on the order that actually processes it later).
  // Lines with no subscription start date always count either way.
  var COUNT_SUBSCRIPTION_VOLUME = false;
  var SUBSCRIPTION_DATE_KEYS = ['__uwallet_subscriptions_start', 'My_Subscription_Date'];

  // Product metafields that hold each volume (Retail / Business / Wholesale).
  var VOLUME_METAFIELD_NAMESPACE = 'apex';
  var VOLUME_METAFIELDS = [
    { alias: 'rv', key: 'volume1' },
    { alias: 'bv', key: 'volume2' },
    { alias: 'wv', key: 'volume3' }
  ];

  function readConfig() {
    var node = document.getElementById(CONFIG_NODE_ID);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function getCookie(name) {
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      var idx = parts[i].indexOf('=');
      var k = idx === -1 ? parts[i] : parts[i].slice(0, idx);
      if (k === name) {
        return decodeURIComponent(parts[i].slice(idx + 1));
      }
    }
    return '';
  }

  function urlParam(name) {
    try {
      return new URL(window.location.href).searchParams.get(name) || '';
    } catch (e) {
      return '';
    }
  }

  function num(v) {
    if (v == null) return 0;
    var n = parseFloat(v);
    return isFinite(n) ? n : 0;
  }

  function round4(n) {
    return Math.round(n * 10000) / 10000;
  }

  // Truncates a Business Volume (volume2) line total to a whole number. Rounding to 6 decimals first only strips float noise (0.29 * 100 = 28.999…), so the rule stays truncation: 25.8 → 25.
  function truncVolume(n) {
    return Math.trunc(Math.round(n * 1e6) / 1e6);
  }

  // Local calendar date as YYYY-MM-DD (matches the flatpickr-picked dates).
  function todayISO() {
    var d = new Date();
    var mo = d.getMonth() + 1;
    var da = d.getDate();
    return d.getFullYear() + '-' + (mo < 10 ? '0' + mo : mo) + '-' + (da < 10 ? '0' + da : da);
  }

  // A line's subscription start date (YYYY-MM-DD) if it carries one, else ''.
  function subscriptionStart(props) {
    for (var i = 0; i < SUBSCRIPTION_DATE_KEYS.length; i++) {
      var v = props[SUBSCRIPTION_DATE_KEYS[i]];
      if (v) return String(v).slice(0, 10);
    }
    return '';
  }

  // True when a line explicitly carries its own volume property set to 0.
  // membership.js writes _Retail Volume / Business Volume / _Wholesale Volume
  // per line at add-to-cart time from that specific product-item tile's own
  // metafields — e.g. a paired "Subscription" fulfillment SKU whose Business
  // Volume is deliberately 0 because the real BV is credited via a companion
  // "Affiliate" line instead. The product-level metafield refetch below can't
  // see that per-line intent, so an explicit 0 here overrides it. This can
  // only ever pull a line's volume DOWN to 0, never inflate it, so it can't be
  // used to claim extra volume even if a line's properties were tampered with.
  function linePropertyIsZero(props, key) {
    var v = props[key];
    return v !== undefined && v !== null && v !== '' && num(v) === 0;
  }

  // Whether a line's volume counts toward the totals — see COUNT_SUBSCRIPTION_VOLUME.
  // Free/bonus lines never count, regardless of subscription date: either
  // flagged by membership.js's auto-applied free product/gift properties, or
  // priced at 0 (e.g. a $0 subscription spray tile that carries neither
  // property but still has its own non-zero apex.volume1/2/3 metafield).
  // Non-subscription lines (no start date) always count; a subscription
  // counts only when the toggle is on, or when it starts today.
  function lineCountsVolume(props, item) {
    if (props['_Free Product'] || props['_Free Gift']) return false;
    if (num(item.price) === 0) return false;
    if (COUNT_SUBSCRIPTION_VOLUME) return true;
    var start = subscriptionStart(props);
    if (!start) return true;
    return start === todayISO();
  }

  // --- Text value resolvers (cookies / URL / DOM — never cart attributes) ---

  // apex.sponsor = the resolved sponsor rep #, in every scenario (widget-selected
  // for a manual "Sponsor Lookup", or API-resolved from a ?from / ?cilikey link).
  // The theme resolves it into the sponsor_id cookie (via cbd.kwik.dev); we mirror
  // that cookie. The referral_key / referral_value pair (below) records how the
  // visitor arrived.
  function readSponsor() {
    return (getCookie('sponsor_id') || '').trim();
  }

  // ---- Referral key + value ------------------------------------------------
  // Supersedes the old share_key + from_source metafields. referral_key labels
  // HOW the visitor arrived; referral_value is the raw identifier (?from or
  // ?cilikey value), or — for a manual sponsor lookup with no referral link —
  // the selected sponsor id. Decision tree (see the enrollment scenarios sheet):
  //   ?cilikey=…                              → "Custom Share Link"           value = cilikey
  //   ?from=… & type=sponsor                  → "Sponsor-led Enrollment"      value = from
  //   ?from=… & #reset on customer-product    → "Customer Share Link"         value = from
  //   ?from=… & #reset (other pages)          → "Distributor Enrollment Link" value = from
  //   ?from=… (plain)                         → "Replicated Web Site Link"    value = from
  //   no referral param + manual lookup       → "Sponsor Lookup"              value = sponsor id
  var LS_REFERRAL = 'apex_referral';

  // Referral from the CURRENT URL context. null when this load carries no
  // referral link (the sponsor-lookup path is handled in readReferral()).
  function referralFromUrl() {
    var cilikey = (urlParam('cilikey') || urlParam('ciliprivatekey') || '').trim();
    if (cilikey) return { key: 'Custom Share Link', value: cilikey };

    var from = (urlParam('from') || '').trim();
    if (from) {
      var type = (urlParam('type') || '').toLowerCase();
      var hasReset = (window.location.hash || '').toLowerCase().indexOf('reset') !== -1;
      var path = window.location.pathname.toLowerCase();
      if (type === 'sponsor') return { key: 'Sponsor-led Enrollment', value: from };
      if (hasReset && path.indexOf('customer-product') !== -1) return { key: 'Customer Share Link', value: from };
      if (hasReset) return { key: 'Distributor Enrollment Link', value: from };
      return { key: 'Replicated Web Site Link', value: from };
    }
    return null;
  }

  // Persist on landing so the referral survives resolveAndSetRep()'s strip-and-
  // reload (which removes ?from / ?cilikey from the URL a moment after load).
  function captureReferral() {
    var r = referralFromUrl();
    if (!r) return;
    try { localStorage.setItem(LS_REFERRAL, JSON.stringify(r)); } catch (e) {}
  }

  function readReferral() {
    try {
      var raw = localStorage.getItem(LS_REFERRAL);
      if (raw) {
        var r = JSON.parse(raw);
        if (r && r.key) return { key: r.key, value: (r.value || '').trim() };
      }
    } catch (e) {}
    // No referral link → sponsor-lookup widget (keyed once a sponsor is selected).
    var sponsor = readSponsor();
    if (sponsor) return { key: 'Sponsor Lookup', value: sponsor };
    return { key: '', value: '' };
  }

  function readDeliveryDate() {
    var el = document.getElementById('deliveryDate');
    return el && el.value ? String(el.value).trim() : '';
  }

  // Same source from-param-tracker.liquid writes the enrollment_type cart
  // attribute from, so metafield and attribute always carry the same value.
  function readEnrollmentType() {
    try {
      return (localStorage.getItem('enrollment_type') || '').trim();
    } catch (e) {
      return '';
    }
  }

  function pick(overrideVal, fallback) {
    var v = overrideVal == null ? '' : String(overrideVal).trim();
    return v || fallback;
  }

  function sfQuery(token, query, variables) {
    return fetch(SF_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: query, variables: variables })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // Fetch each cart product's volume metafields (apex.volume1/2/3)
  // and return a map keyed by numeric product id → { rv, bv, wv } (per-unit).
  // Stable source: product metafields aren't affected by discounts, cart
  // transforms, or attribute overwrites the way line-item data is.
  function fetchProductVolumeMap(token, items) {
    var ids = [];
    var seen = {};
    for (var i = 0; i < items.length; i++) {
      var pid = items[i].product_id;
      if (!pid || seen[pid]) continue;
      seen[pid] = true;
      ids.push('gid://shopify/Product/' + pid);
    }
    if (!ids.length) return Promise.resolve({});

    var fields = '';
    for (var f = 0; f < VOLUME_METAFIELDS.length; f++) {
      fields +=
        VOLUME_METAFIELDS[f].alias +
        ': metafield(namespace: "' +
        VOLUME_METAFIELD_NAMESPACE +
        '", key: "' +
        VOLUME_METAFIELDS[f].key +
        '") { value } ';
    }
    var query =
      'query apexVolumes($ids: [ID!]!) { nodes(ids: $ids) { ... on Product { id ' +
      fields +
      '} } }';

    return sfQuery(token, query, { ids: ids }).then(function (res) {
      var nodes = res && res.data && res.data.nodes ? res.data.nodes : [];
      var map = {};
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (!n || !n.id) continue;
        var pid = n.id.split('/').pop();
        map[pid] = {
          rv: n.rv ? num(n.rv.value) : 0,
          bv: n.bv ? num(n.bv.value) : 0,
          wv: n.wv ? num(n.wv.value) : 0
        };
      }
      return map;
    });
  }

  // Enrich every line item with its per-unit product-metafield volumes and the
  // per-line totals (per-unit × quantity). This is the record the JSON metafield
  // stores; volume1/2/3 are the sums of the *_total fields here.
  function buildLineItems(items, map) {
    map = map || {};
    return (items || []).map(function (item) {
      var props = item.properties || {};
      var apiVol = map[item.product_id] || {};
      // Fall back to the line's own volume properties (stamped by Liquid at
      // add-to-cart time — buy-buttons.liquid / card-product.liquid /
      // membership.js) whenever the Storefront product lookup has no usable
      // value for this product, e.g. the Storefront API call failed.
      var rv = apiVol.rv || num(props['_Retail Volume']);
      var bv = apiVol.bv || num(props['Business Volume']);
      var wv = apiVol.wv || num(props['_Wholesale Volume']);
      var qty = num(item.quantity);
      var counted = lineCountsVolume(props, item);
      var rvCounted = counted && !linePropertyIsZero(props, '_Retail Volume');
      var bvCounted = counted && !linePropertyIsZero(props, 'Business Volume');
      var wvCounted = counted && !linePropertyIsZero(props, '_Wholesale Volume');
      return {
        key: item.key,
        variant_id: item.variant_id || item.id,
        product_id: item.product_id,
        quantity: item.quantity,
        properties: props,
        volume1: round4(rv),
        volume2: round4(bv),
        volume3: round4(wv),
        counted: counted,
        // Only Business Volume is a whole number: truncate its line total
        // (unit × qty). RV and WV keep 4-decimal rounding. Per-unit source
        // values above stay as-is.
        volume1_total: rvCounted ? round4(rv * qty) : 0,
        volume2_total: bvCounted ? truncVolume(bv * qty) : 0,
        volume3_total: wvCounted ? round4(wv * qty) : 0
      };
    });
  }

  // Totals are summed straight from the enriched line records, guaranteeing
  // volume1/2/3 === Σ of the per-line *_total values in the JSON metafield.
  function sumVolumes(lineItems) {
    var v1 = 0, v2 = 0, v3 = 0;
    for (var i = 0; i < lineItems.length; i++) {
      v1 += lineItems[i].volume1_total;
      v2 += lineItems[i].volume2_total;
      v3 += lineItems[i].volume3_total;
    }
    // round4 strips float noise from summing decimal RV/WV totals.
    return { v1: round4(v1), v2: v2, v3: round4(v3) };
  }

  // `overrides` (optional) supplies text values directly (enrollment finale).
  // Volumes always derive from the product metafields so the totals and the
  // per-line JSON stay in lock-step.
  function buildMetafields(ownerId, lineItems, vol, overrides, hasMemberId, cartHasItems) {
    overrides = overrides || {};

    var sponsor = pick(overrides.sponsor, readSponsor());
    var referral = readReferral();
    var referralKey = pick(overrides.referral_key, referral.key);
    var referralValue = pick(overrides.referral_value, referral.value);
    var deliveryDate = pick(overrides.delivery_date, readDeliveryDate());
    var enrollmentType = pick(overrides.enrollment_type, readEnrollmentType());

    // Nothing worth persisting yet — skip the write entirely. Keyed on the
    // cart's items rather than lineItems: on a skip_volumes write lineItems is
    // empty by design, but a non-empty cart still needs apex.has_member_id.
    // enrollmentType counts here too, so a self-enrollment with no
    // sponsor/referral/date can't drop it.
    if (!cartHasItems && !sponsor && !referralKey && !referralValue && !deliveryDate && !enrollmentType) {
      return null;
    }

    // skip_volumes writes the attribution text fields and the member flag
    // ONLY. It deliberately does not emit cart_lines/volume1/2/3 as
    // empty-or-zero: cartMetafieldsSet
    // is an upsert, so writing zeros would clobber whatever a background sync
    // had already computed for this cart. Omitting the keys leaves them alone.
    var metafields = overrides.skip_volumes ? [] : [
      { ownerId: ownerId, key: 'apex.cart_lines', type: 'json', value: JSON.stringify(lineItems) },
      { ownerId: ownerId, key: 'apex.volume1', type: 'number_decimal', value: String(vol.v1) },
      { ownerId: ownerId, key: 'apex.volume2', type: 'number_decimal', value: String(vol.v2) },
      { ownerId: ownerId, key: 'apex.volume3', type: 'number_decimal', value: String(vol.v3) }
    ];

    var text = [
      ['apex.sponsor', sponsor],
      ['apex.referral_key', referralKey],
      ['apex.referral_value', referralValue],
      ['apex.delivery_date', deliveryDate],
      ['apex.enrollment_type', enrollmentType]
    ];
    for (var i = 0; i < text.length; i++) {
      if (text[i][1]) {
        metafields.push({
          ownerId: ownerId,
          key: text[i][0],
          type: 'single_line_text_field',
          value: text[i][1]
        });
      }
    }

    // Always written, true or false: cartMetafieldsSet is an upsert, so false
    // is what clears a flag left over from a previous login.
    metafields.push({
      ownerId: ownerId,
      key: 'apex.has_member_id',
      type: 'boolean',
      value: hasMemberId ? 'true' : 'false'
    });

    return metafields;
  }

  // Resolves `true` on a confirmed successful write (or a no-op dedupe skip),
  // `false` when Shopify rejected it — callers that need to know whether the
  // write actually landed (the checkout-time override) check this instead of
  // assuming a resolved promise always means success.
  function writeMetafields(token, metafields) {
    var syncHash = '';
    try {
      syncHash = JSON.stringify(metafields);
      if (sessionStorage.getItem(SS_MF_HASH_KEY) === syncHash) return Promise.resolve(true);
    } catch (e) {}

    var mutation =
      'mutation cartMetafieldsSet($metafields: [CartMetafieldsSetInput!]!) {' +
      ' cartMetafieldsSet(metafields: $metafields) {' +
      ' userErrors { field message code } } }';

    return sfQuery(token, mutation, { metafields: metafields }).then(function (res) {
      var topLevelErrors = res && res.errors;
      var userErrors = res && res.data && res.data.cartMetafieldsSet && res.data.cartMetafieldsSet.userErrors;
      if (topLevelErrors && topLevelErrors.length) {
        console.error('[apex] cartMetafieldsSet top-level errors:', topLevelErrors, 'full response:', res);
      }
      if (userErrors && userErrors.length) {
        console.error('[apex] cartMetafieldsSet userErrors:', userErrors, 'full response:', res);
      }
      if (!res) {
        console.error('[apex] cartMetafieldsSet request failed (no response) — check network tab.');
      }

      var failed = !res || (topLevelErrors && topLevelErrors.length) || (userErrors && userErrors.length);
      if (failed) return false;

      if (syncHash) {
        try {
          sessionStorage.setItem(SS_MF_HASH_KEY, syncHash);
        } catch (e) {}
      }
      return true;
    });
  }

  // Mirrors the cart token into the CartToken cart attribute. Already-matching
  // attributes are left alone, so this doesn't re-write on every sync.
  function setCartTokenAttribute(cart) {
    if ((cart.attributes || {})[CART_TOKEN_ATTRIBUTE] === cart.token) return Promise.resolve();

    var attributes = {};
    attributes[CART_TOKEN_ATTRIBUTE] = cart.token;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attributes })
    }).catch(function () {});
  }

  // Resolves with the computed { v1, v2, v3 } totals (same numbers written to
  // apex.volume1/2/3) so callers — namely membership.js's checkout finale —
  // can mirror them into the "Total RV/BV/WV" cart attributes themselves.
  // This file stays metafields-only; it does not write cart attributes.
  //
  // Resolves with `null` (never rejects) when a write was attempted and
  // Shopify rejected it — background syncs (syncFromServer,
  // __APEX_SYNC_CART_METAFIELDS__) just see no result and log via
  // writeMetafields' console.error. The checkout-finale wrapper below is what
  // turns a `null` here into a thrown error, since that call is the last
  // chance to correct cart_lines/volumes before the order is placed.
  function setCartMetafield(config, cart, overrides) {
    var token = config.storefront_api_token;
    if (!token || !cart || !cart.token) return Promise.resolve(null);
    overrides = overrides || {};

    setCartTokenAttribute(cart);

    var ownerId = 'gid://shopify/Cart/' + cart.token;
    var items = cart.items || [];
    var hasMemberId = config.has_member_id === true;
    var cartHasItems = items.length > 0;

    // The product-metafield lookup below exists only to compute volumes, so
    // skip_volumes skips that whole round trip and writes the attribution text
    // fields straight from the overrides. Resolves with a marker that is
    // deliberately NOT shaped like { v1, v2, v3 }, so a caller still expecting
    // volume totals fails loudly instead of silently writing 0.00.
    if (overrides.skip_volumes) {
      var textOnly = buildMetafields(ownerId, [], null, overrides, hasMemberId, cartHasItems);
      // Nothing to persist (empty cart, no sponsor/referral/date) is a successful no-op.
      if (!textOnly) return Promise.resolve({ skipped_volumes: true });
      return writeMetafields(token, textOnly).then(function (ok) {
        return ok ? { skipped_volumes: true } : null;
      });
    }

    return fetchProductVolumeMap(token, items).then(function (map) {
      var lineItems = buildLineItems(items, map);
      var vol = sumVolumes(lineItems);
      var metafields = buildMetafields(ownerId, lineItems, vol, overrides, hasMemberId, cartHasItems);
      if (!metafields) return vol;
      return writeMetafields(token, metafields).then(function (ok) {
        return ok ? vol : null;
      });
    });
  }

  // no-store: a cached GET here is indistinguishable from a genuinely stale
  // cart and is exactly what the checkout finale (the last read before an
  // order is placed) can't tolerate. The timestamp query param defeats any
  // URL-keyed caching between here and Shopify (CDN/edge/dev-tunnel) that
  // `cache: 'no-store'` alone can't reach, since that option only governs
  // the browser's own HTTP cache.
  function fetchCart() {
    return fetch('/cart.js?_apex_ts=' + Date.now(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // True once every variant in `expected` (a variant_id -> quantity map) is
  // present in the cart with at least that quantity. Per-variant rather than
  // a single total: two lines being off in opposite directions (one short,
  // one over) by the same amount would pass a total-only check while still
  // computing volumes from the wrong per-line quantities.
  function cartMeetsExpectedQuantities(cart, expected) {
    var items = (cart && cart.items) || [];
    var totals = {};
    for (var i = 0; i < items.length; i++) {
      var vid = items[i].variant_id || items[i].id;
      totals[vid] = (totals[vid] || 0) + num(items[i].quantity);
    }
    for (var key in expected) {
      if (!Object.prototype.hasOwnProperty.call(expected, key)) continue;
      if ((totals[key] || 0) < expected[key]) return false;
    }
    return true;
  }

  function syncFromServer(config) {
    // All automatic syncs stand down once enrollment is authoritative; the
    // explicit override write uses fetchCart() directly and is unaffected.
    if (window.__APEX_SKIP_AUTO_SYNC__) return Promise.resolve();
    return fetchCart().then(function (cart) {
      if (cart) return setCartMetafield(config, cart);
    });
  }

  function run() {
    var config = readConfig();
    if (!config || !config.storefront_api_token) return;
    window.__APEX_CONFIG__ = config;

    // Exposed for apex-cart-volume.js to re-sync after cart mutations. Called
    // with a cart object (avoids a refetch) or with nothing (fetch then sync).
    // Suppressed once the enrollment flow has taken authoritative control, so a
    // late generic recompute can't overwrite the final chosen state.
    window.__APEX_SYNC_CART_METAFIELDS__ = function (cart) {
      if (window.__APEX_SKIP_AUTO_SYNC__) return;
      if (cart) setCartMetafield(config, cart);
      else syncFromServer(config);
    };

    // Explicit write used by the enrollment finale (see membership.js) to push
    // the chosen state into the cart metafields right before redirecting to
    // /checkout. Volumes still come from the product metafields; overrides here
    // are for the text values (sponsor, delivery date). Returns a promise that
    // REJECTS if the cart couldn't be read or the write was rejected by
    // Shopify — unlike the automatic background syncs, this call is the last
    // chance to correct cart_lines/volumes before the order is placed, so the
    // caller (membership.js) needs to know it actually landed, not just that
    // the promise settled. Resolves with the { v1, v2, v3 } totals on success.
    //
    // overrides.skip_volumes (optional): write the attribution text fields
    // (sponsor, referral_key, referral_value, delivery_date, enrollment_type)
    // plus apex.has_member_id, and nothing else. cart_lines and volume1/2/3
    // are left untouched rather than zeroed. This is what the enrollment finale uses: it skips both the
    // expected_quantities wait and the product-volume lookup, which were the
    // two slowest steps between the final click and /checkout. The cart is
    // still read, but only for its token.
    //
    // overrides.expected_quantities (optional, unused by the enrollment finale
    // since it passes skip_volumes): { variant_id: quantity } for
    // what membership.js actually just posted to /cart/add.js. The GET
    // /cart.js immediately after a large multi-item /cart/add.js can return
    // a still-settling cart — correct line shape, but one or more lines
    // short their real quantity — confirmed at runtime via a DevTools
    // breakpoint. When given, retry the fetch (capped, short backoff) until
    // every expected variant's quantity has caught up, so volumes are never
    // computed from a partial snapshot. Falls back to the last fetch after
    // the cap so a genuine mismatch (e.g. an item Shopify rejected) can't
    // hang the checkout.
    //
    // Only relevant to a volume-writing call. Runtime-measured back when the
    // enrollment finale computed volumes: a 5-try / 300ms budget (~1.5s) was not enough — a
    // real cart (subscription + membership + PRO PACK posted in one
    // multi-item /cart/add.js) was still short a line's quantity after every
    // retry in that window. 10 tries / 400ms (~4s) is the smallest bump that
    // covers the observed delay while staying a short, bounded wait.
    var EXPECTED_QTY_RETRY_MS = 400;
    var EXPECTED_QTY_MAX_RETRIES = 10;

    // Which expected variants the cart is still short, for diagnostics.
    function expectedQuantityShortfall(cart, expected) {
      var items = (cart && cart.items) || [];
      var totals = {};
      for (var i = 0; i < items.length; i++) {
        var vid = items[i].variant_id || items[i].id;
        totals[vid] = (totals[vid] || 0) + num(items[i].quantity);
      }
      var short = {};
      for (var key in expected) {
        if (!Object.prototype.hasOwnProperty.call(expected, key)) continue;
        var have = totals[key] || 0;
        if (have < expected[key]) short[key] = { expected: expected[key], inCart: have };
      }
      return short;
    }

    window.__APEX_WRITE_CART_METAFIELDS__ = function (overrides) {
      window.__APEX_SKIP_AUTO_SYNC__ = true;
      overrides = overrides || {};
      var expectedQuantities = overrides.expected_quantities;

      // This loop exits as soon as the cart matches, so a settled cart costs one
      // GET and no delay — the ~4s cap is worst case, not the normal path. But a
      // line Shopify never fulfils (rejected/sold-out variant, id mismatch) can
      // never match, so the full cap is burned on EVERY checkout while the
      // fallback quietly proceeds. Report which case actually happened, so a slow
      // redirect can be told apart from a genuinely settling cart.
      var startedAt = Date.now();

      function attempt(triesLeft) {
        return fetchCart().then(function (cart) {
          if (!cart) {
            throw new Error('apex: fetchCart() returned no cart before the final metafield write');
          }
          if (expectedQuantities && !cartMeetsExpectedQuantities(cart, expectedQuantities) && triesLeft > 0) {
            return new Promise(function (resolve) {
              setTimeout(resolve, EXPECTED_QTY_RETRY_MS);
            }).then(function () {
              return attempt(triesLeft - 1);
            });
          }

          var retriesUsed = EXPECTED_QTY_MAX_RETRIES - triesLeft;
          var waitedMs = Date.now() - startedAt;

          if (!expectedQuantities) {
            // No quantity gate — nothing was waited on, so there is nothing to
            // report. This is the skip_volumes path: the cart is read for its
            // token only, and a partial cart can't affect a text-only write.
          } else if (!cartMeetsExpectedQuantities(cart, expectedQuantities)) {
            // Cap exhausted and still short: this checkout paid the full delay
            // for nothing and volumes come from a partial cart.
            console.warn(
              '[apex] expected cart quantities never matched after ' + retriesUsed +
              ' retries (' + waitedMs + 'ms) — proceeding with a partial cart; ' +
              'volumes may be wrong. Short by:',
              expectedQuantityShortfall(cart, expectedQuantities)
            );
          } else if (retriesUsed > 0) {
            console.log('[apex] cart settled after ' + retriesUsed + ' retries (' + waitedMs + 'ms)');
          } else {
            console.log('[apex] cart settled on first fetch (' + waitedMs + 'ms, no retry delay)');
          }

          return setCartMetafield(config, cart, overrides).then(function (vol) {
            if (!vol) {
              throw new Error('apex: final cartMetafieldsSet write was rejected — see prior [apex] console errors');
            }
            return vol;
          });
        });
      }

      return attempt(EXPECTED_QTY_MAX_RETRIES);
    };

    // Capture the referral (key + value) from the URL immediately, before
    // resolveAndSetRep() strips ?from / ?cilikey and reloads.
    captureReferral();

    syncFromServer(config);

    // sponsor_id cookie is written asynchronously by cart-attributes.liquid (~2s)
    // and the deliveryDate field can populate late, so re-sync once more shortly
    // after load. The payload hash dedupes no-ops.
    setTimeout(function () { syncFromServer(config); }, LATE_SYNC_MS);

    // Re-sync when the preferred-date field changes.
    document.addEventListener('change', function (e) {
      if (e.target && e.target.id === 'deliveryDate') syncFromServer(config);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
